import { useEffect, useState } from "react";
import { toast } from "sonner";
import ReactMarkdown from "react-markdown";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth.jsx";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { BookOpen, Pencil, Save, X, Loader2 } from "lucide-react";

export default function HandlingGuide() {
    const { user } = useAuth();
    const isAdmin = user?.role === "admin";
    const [content, setContent] = useState("");
    const [editing, setEditing] = useState(false);
    const [draft, setDraft] = useState("");
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);

    const load = async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/meta/handling-guide");
            setContent(data.content || "");
        } catch (e) {
            toast.error("Kunne ikke hente handleplanen");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); }, []);

    const startEdit = () => {
        setDraft(content);
        setEditing(true);
    };

    const cancel = () => {
        setDraft("");
        setEditing(false);
    };

    const save = async () => {
        setSaving(true);
        try {
            await api.put("/meta/handling-guide", { content: draft });
            setContent(draft);
            setEditing(false);
            toast.success("Handleplanen er gemt");
        } catch (e) {
            toast.error(e.response?.data?.detail || "Kunne ikke gemme");
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="space-y-6" data-testid="handling-guide-page">
            <div className="flex items-center justify-between gap-4 flex-wrap">
                <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-[#F0EBE1] flex items-center justify-center">
                        <BookOpen className="w-5 h-5 text-[#4A4641]" />
                    </div>
                    <div>
                        <h1 className="font-heading text-3xl font-medium text-[#2C2A29]">Handleplan</h1>
                        <p className="text-sm text-[#78736E] mt-1">Vejledning til håndtering af reklamationer.</p>
                    </div>
                </div>
                {isAdmin && !editing && (
                    <Button onClick={startEdit} variant="outline" className="border-[#E5E1DA] hover:bg-[#F0EBE1]" data-testid="edit-guide-button">
                        <Pencil className="w-4 h-4 mr-2" />
                        Rediger
                    </Button>
                )}
                {isAdmin && editing && (
                    <div className="flex gap-2">
                        <Button onClick={cancel} variant="outline" className="border-[#E5E1DA]" data-testid="cancel-guide-button">
                            <X className="w-4 h-4 mr-2" />
                            Annullér
                        </Button>
                        <Button onClick={save} disabled={saving} className="bg-[#4A4641] hover:bg-[#2C2A29] text-white" data-testid="save-guide-button">
                            {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                            Gem
                        </Button>
                    </div>
                )}
            </div>

            <div className="bg-white border border-[#E5E1DA] rounded-2xl shadow-[0_8px_32px_rgba(44,42,41,0.04)] overflow-hidden">
                {loading && (
                    <div className="p-12 text-center text-[#78736E]">
                        <Loader2 className="w-5 h-5 animate-spin mx-auto mb-2" />
                        Indlæser…
                    </div>
                )}
                {!loading && editing && (
                    <div className="grid grid-cols-1 lg:grid-cols-2">
                        <div className="border-r border-[#E5E1DA] p-5">
                            <div className="text-xs font-semibold uppercase tracking-wider text-[#78736E] mb-2">Markdown</div>
                            <Textarea
                                rows={28}
                                value={draft}
                                onChange={(e) => setDraft(e.target.value)}
                                className="font-mono text-sm border-[#E5E1DA] resize-y"
                                data-testid="guide-textarea"
                            />
                            <div className="text-xs text-[#78736E] mt-2">
                                Brug Markdown: <code># Overskrift</code> · <code>## Underoverskrift</code> · <code>**fed**</code> · <code>&gt; citat</code> · <code>- punkt</code>
                            </div>
                        </div>
                        <div className="p-5 lg:p-8 bg-[#FAFAF9] overflow-y-auto max-h-[80vh]">
                            <div className="text-xs font-semibold uppercase tracking-wider text-[#78736E] mb-3">Forhåndsvisning</div>
                            <article className="prose prose-stone max-w-none guide-markdown">
                                <ReactMarkdown>{draft}</ReactMarkdown>
                            </article>
                        </div>
                    </div>
                )}
                {!loading && !editing && (
                    <article className="prose prose-stone max-w-none guide-markdown p-6 sm:p-10" data-testid="guide-content">
                        <ReactMarkdown>{content}</ReactMarkdown>
                    </article>
                )}
            </div>
        </div>
    );
}
